import type { jsPDF } from 'jspdf'
import type { CellDef, Styles } from 'jspdf-autotable'
import { DISPLAY_LOCALE } from '@dcp/shared/utils/formatters'
import type { CashPositionReport, ReportColumn } from './cashPositionReport'
import { paginateCashPositionReport, type ReportPage } from './cashPositionReportPages'
import {
  reportBlockRowStyles,
  reportHeaderStyle,
  reportSectionCaption,
  reportTopLine,
  reportValueCellStyle,
  type ReportCellStyle,
  type ReportTopLineCell,
} from './cashPositionReportStyle'

// US Legal landscape, in points: the customer's printed report is Legal.
const PAGE_WIDTH = 1008
const PAGE_HEIGHT = 612
const MARGIN = 18
/** Wide enough for "Minimum Daily Bank Balance" and "CASH (Book) + CREDIT AVAILABLE". */
const LABEL_COLUMN_WIDTH = 150
/** Fits "(12,345,678)" at the body font, and gives 14 data columns on a page. */
const DATA_COLUMN_WIDTH = 58
const FONT_SIZE = 7
/** Section headings and upper-case rows read one step larger. */
const EMPHASIS_FONT_SIZE = 8
/** Fixed row height so the row budget is exact: 8pt text (1.15 line factor) plus padding fits. */
const ROW_HEIGHT = 9.5
const CELL_PADDING = { top: 0.15, bottom: 0.15, left: 3, right: 3 }
/** The header row holds two lines: a wrapped facility name, or a pack name over "Total". */
const HEAD_LINES = 2
const HEAD_HEIGHT = HEAD_LINES * ROW_HEIGHT
/** The "Most Recently Recorded Date" line plus a gap; reserved on every page so all pages share one budget. */
const TOP_LINE_HEIGHT = 16
const GROUP_RULE_WIDTH = 0.75

const MAX_DATA_COLUMNS = Math.floor((PAGE_WIDTH - 2 * MARGIN - LABEL_COLUMN_WIDTH) / DATA_COLUMN_WIDTH)
const MAX_ROWS = Math.floor((PAGE_HEIGHT - 2 * MARGIN - TOP_LINE_HEIGHT - HEAD_HEIGHT) / ROW_HEIGHT)

const BLACK = '#000000'
/** Group rules in the header, white on the dark fills. */
const HEAD_RULE = '#FFFFFF'

const wholeDollars = new Intl.NumberFormat(DISPLAY_LOCALE, { maximumFractionDigits: 0 })

/**
 * Formats a report amount the way the printed report shows it: whole dollars
 * with thousands separators, negatives in parentheses, no currency sign; zero
 * (including amounts that round to zero) and missing values print as "-".
 */
export function formatReportAmount(value: number | null): string {
  if (value === null) return '-'
  const rounded = Math.round(Math.abs(value))
  if (rounded === 0) return '-'
  const text = wholeDollars.format(rounded)
  return value < 0 ? `(${text})` : text
}

/**
 * Text measuring in the header font (helvetica bold at the body size), injected
 * so the layout can be computed without a jsPDF document.
 */
export interface PdfTextMeasure {
  /** Word-wraps `text` to lines no wider than `width` points. */
  splitText(text: string, width: number): string[]
  /** Width of `text` in points. */
  textWidth(text: string): number
}

/** Everything one PDF page draws: the optional top line and one table. */
export interface PdfPageLayout {
  /** The "Most Recently Recorded Date" line; only on the first row slice of a column slice. */
  topLine: ReportTopLineCell[] | null
  tableWidth: number
  columnStyles: Record<string, Partial<Styles>>
  /** One header row: the label column, then one cell per data column on the page. */
  head: CellDef[]
  /** The rows of the page's blocks, label cell first. */
  body: CellDef[][]
}

/**
 * Lays the report out on US Legal landscape pages: splits it with
 * `paginateCashPositionReport` and turns every page into the header and body
 * cells autotable draws, including the fitted header lines and the rules that
 * separate packs and standalone facilities.
 */
export function layoutCashPositionPdf(report: CashPositionReport, measure: PdfTextMeasure): PdfPageLayout[] {
  const pages = paginateCashPositionReport(report, {
    maxDataColumns: MAX_DATA_COLUMNS,
    maxRows: MAX_ROWS,
  })
  return pages.map((page) => {
    const columns = page.columns.map((i) => report.columns[i])
    const groupStarts = groupStartFlags(report.columns, page.columns)
    return {
      topLine: page.first ? reportTopLine(report.asOf) : null,
      tableWidth: LABEL_COLUMN_WIDTH + columns.length * DATA_COLUMN_WIDTH,
      columnStyles: columnStyles(columns.length),
      head: headRow(measure, columns, groupStarts),
      body: bodyRows(report, page, groupStarts),
    }
  })
}

/**
 * Renders the Cash Position Summary report as a US Legal landscape PDF.
 *
 * jsPDF and its table plugin are loaded on demand so they stay out of the main
 * bundle. Each page of `layoutCashPositionPdf` is one table that repeats the
 * label column.
 */
export async function buildCashPositionPdf(report: CashPositionReport): Promise<Blob> {
  const [{ jsPDF }, { autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')])
  const doc = new jsPDF({ format: 'legal', orientation: 'landscape', unit: 'pt' })
  // The whole layout is measured before anything is drawn, so the header font
  // set here is the one every measurement sees.
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(FONT_SIZE)
  const layout = layoutCashPositionPdf(report, {
    splitText: (text, width) => {
      const split: unknown = doc.splitTextToSize(text, width)
      return Array.isArray(split) ? split.map(String) : [text]
    },
    textWidth: (text) => doc.getTextWidth(text),
  })

  layout.forEach((page, index) => {
    if (index > 0) doc.addPage()
    if (page.topLine) drawTopLine(doc, page.topLine)
    autoTable(doc, {
      theme: 'plain',
      startY: MARGIN + TOP_LINE_HEIGHT,
      margin: { top: MARGIN, bottom: MARGIN, left: MARGIN, right: MARGIN },
      tableWidth: page.tableWidth,
      // The pages are sized to fit; this only matters for a block taller than a
      // page, which then continues on the next page under a repeated header.
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      showHead: 'everyPage',
      styles: {
        font: 'helvetica',
        fontSize: FONT_SIZE,
        cellPadding: CELL_PADDING,
        minCellHeight: ROW_HEIGHT,
        valign: 'middle',
        overflow: 'visible',
        lineColor: BLACK,
        lineWidth: 0,
        textColor: BLACK,
      },
      columnStyles: page.columnStyles,
      head: [page.head],
      body: page.body,
    })
  })

  return doc.output('blob')
}

function drawTopLine(doc: jsPDF, cells: ReportTopLineCell[]): void {
  const height = ROW_HEIGHT + 2
  const baseline = MARGIN + height - 3.5
  doc.setFontSize(FONT_SIZE)
  // The first box spans the label column; the others fit their text.
  let x = MARGIN
  cells.forEach(({ text, style }, i) => {
    doc.setFont('helvetica', fontStyleOf(style))
    const boxWidth = i === 0 ? LABEL_COLUMN_WIDTH : doc.getTextWidth(text) + CELL_PADDING.left + CELL_PADDING.right
    if (style.fill) {
      doc.setFillColor(style.fill)
      doc.rect(x, MARGIN, boxWidth, height, 'F')
    }
    doc.setTextColor(style.textColor)
    doc.text(text, x + CELL_PADDING.left, baseline)
    x += boxWidth
  })
}

/** Which of the page's columns open a group, so a rule can separate packs and standalones. */
function groupStartFlags(all: ReportColumn[], indices: number[]): boolean[] {
  return indices.map((index, position) => position === 0 || all[indices[position - 1]].groupKey !== all[index].groupKey)
}

function columnStyles(dataColumns: number): Record<string, Partial<Styles>> {
  const styles: Record<string, Partial<Styles>> = {
    0: { cellWidth: LABEL_COLUMN_WIDTH, halign: 'left' },
  }
  for (let i = 1; i <= dataColumns; i++) styles[i] = { cellWidth: DATA_COLUMN_WIDTH, halign: 'right' }
  return styles
}

function headRow(measure: PdfTextMeasure, columns: ReportColumn[], groupStarts: boolean[]): CellDef[] {
  // Lines are fitted here rather than by autotable so the header can never grow
  // past the two lines the row budget reserves for it.
  const textWidth = DATA_COLUMN_WIDTH - CELL_PADDING.left - CELL_PADDING.right
  const base: Partial<Styles> = { minCellHeight: HEAD_HEIGHT, valign: 'bottom', lineColor: HEAD_RULE }
  const cells: CellDef[] = [
    {
      content: '',
      styles: { ...base, ...cellStyles(reportHeaderStyle(null)), lineWidth: { right: GROUP_RULE_WIDTH } },
    },
  ]
  columns.forEach((column, i) => {
    const style = reportHeaderStyle(column)
    const label = style.upper ? column.label.toUpperCase() : column.label
    const lines =
      column.kind === 'pack'
        ? [...fitLines(measure, label, textWidth, 1), 'Total']
        : fitLines(measure, label, textWidth, HEAD_LINES)
    cells.push({
      // autotable joins a string[] with commas; a newline keeps the lines apart.
      content: lines.join('\n'),
      styles: {
        ...base,
        ...cellStyles(style),
        halign: style.align,
        lineWidth: { left: groupStarts[i] ? GROUP_RULE_WIDTH : 0 },
      },
    })
  })
  return cells
}

/** Wraps `text` to `width` and cuts it at `maxLines`, ending the last kept line with an ellipsis. */
function fitLines(measure: PdfTextMeasure, text: string, width: number, maxLines: number): string[] {
  const lines = measure.splitText(text, width)
  if (lines.length <= maxLines) return lines
  const kept = lines.slice(0, maxLines)
  let last = `${kept[maxLines - 1]}…`
  while (last.length > 1 && measure.textWidth(last) > width) last = `${last.slice(0, -2)}…`
  kept[maxLines - 1] = last
  return kept
}

function bodyRows(report: CashPositionReport, page: ReportPage, groupStarts: boolean[]): CellDef[][] {
  return page.blocks.flatMap((blockIndex) => {
    const block = report.blocks[blockIndex]
    const styles = reportBlockRowStyles(block)
    return block.rows.map((row, rowIndex) => {
      const style = styles[rowIndex]
      const label: CellDef = {
        content: style.label.upper ? row.label.toUpperCase() : row.label,
        styles: { ...cellStyles(style.label), lineWidth: { right: GROUP_RULE_WIDTH } },
      }
      const values: CellDef[] = page.columns.map((columnIndex, i) => ({
        content:
          row.kind === 'section'
            ? (reportSectionCaption(report.columns[columnIndex]) ?? '')
            : formatReportAmount(row.values[columnIndex] ?? null),
        styles: {
          ...cellStyles(reportValueCellStyle(style, report.columns[columnIndex])),
          lineWidth: { left: groupStarts[i] ? GROUP_RULE_WIDTH : 0 },
        },
      }))
      return [label, ...values]
    })
  })
}

/** The shared cell style as autotable styles, with this writer's font sizes. */
function cellStyles(style: ReportCellStyle): Partial<Styles> {
  return {
    fontStyle: fontStyleOf(style),
    fontSize: style.emphasis ? EMPHASIS_FONT_SIZE : FONT_SIZE,
    textColor: style.textColor,
    fillColor: style.fill ?? false,
  }
}

function fontStyleOf(style: ReportCellStyle): 'normal' | 'bold' | 'italic' | 'bolditalic' {
  if (style.bold) return style.italic ? 'bolditalic' : 'bold'
  return style.italic ? 'italic' : 'normal'
}
